import { hasPermission } from '@hq/permission';
import { Button, Result } from 'antd';
import { Link, Navigate, createBrowserRouter, useLocation } from 'react-router-dom';
import { Suspense, lazy, type ReactElement } from 'react';
import AppLayout from '../layouts/AppLayout';
import ApprovalFlows from '../pages/ApprovalFlows';
import AppReleases from '../pages/AppReleases';
import AppVersions from '../pages/AppVersions';
import AuditLogs from '../pages/AuditLogs';
import Login from '../pages/Login';
import OrgConfig from '../pages/OrgConfig';
import OrgGroups from '../pages/OrgGroups';
import OrgManage from '../pages/OrgManage';
import OrgSpace from '../pages/OrgSpace';
import OrgStructure from '../pages/OrgStructure';
import Platform from '../pages/Platform';
import PlatformOverview from '../pages/PlatformOverview';
import MobileEnter from '../pages/MobileEnter';
import Regions from '../pages/Regions';
import ScreenDevices from '../pages/ScreenDevices';
import ScreenScan from '../pages/ScreenScan';
import SystemUsers from '../pages/SystemUsers';
import Tenants from '../pages/Tenants';
import { PLATFORM_ONLY_KEYS, ROUTE_PERMS } from './routePerms';
import { useAuthStore } from '../session';

// 平台大屏（/screen）：图表重、单独懒加载，登录端首屏不携带大屏代码
const ScreenPage = lazy(() => import('../pages/ScreenPage'));

/** 路由守卫：无令牌跳登录；最终权限仍以后端为准（docs/05 §5）。 */
function RequireAuth({ children }: { children: ReactElement }) {
  const token = useAuthStore((state) => state.token);
  const location = useLocation();
  if (!token) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  return children;
}

/**
 * 路由级门禁（纵深防御）：菜单裁剪只是"看不见"，直接敲 URL 仍能进页面。
 * 两道判定，顺序不能反：
 *   ① 代管中屏蔽平台专属路由 —— 代管令牌 pflg=0，这些接口必 403，
 *      且「管理员不裁剪权限」的豁免绝不能把这一条也放过去（否则平台视角被只读令牌闯入）；
 *   ② 权限码（管理员 / level=admin 豁免，与菜单口径一致）。
 */
function RequirePerm({ path, children }: { path: string; children: ReactElement }) {
  const profile = useAuthStore((state) => state.profile);
  const enter = useAuthStore((state) => state.enter);

  const enterActive = Boolean(enter && enter.expiresAt > Date.now());
  if (enterActive && PLATFORM_ONLY_KEYS.has(`/${path}`)) {
    return (
      <Result
        status="403"
        title="代管模式下不可访问"
        subTitle="当前处于代管租户视角（只读令牌），平台专属页面需先退出代管。"
        extra={
          <Link to="/system/orgs">
            <Button type="primary">前往机构管理</Button>
          </Link>
        }
      />
    );
  }

  const required = ROUTE_PERMS[path];
  if (!required?.length || profile?.level === 'admin') return children;

  const allowed = profile?.perms ? required.some((perm) => hasPermission(profile.perms, perm)) : false;
  if (allowed) return children;

  return (
    <Result
      status="403"
      title="无权访问"
      subTitle={`当前账号缺少 ${required.join(' 或 ')} 权限，请联系系统管理员。`}
      extra={
        <Link to="/overview">
          <Button type="primary">返回平台总览</Button>
        </Link>
      }
    />
  );
}

/** 未匹配路由：明确展示 404，而不是静默跳回总览（用户会以为链接点错了）。 */
function NotFound() {
  return (
    <Result
      status="404"
      title="404"
      subTitle="页面不存在或已被移除，请从左侧菜单重新进入。"
      extra={
        <Link to="/overview">
          <Button type="primary">返回平台总览</Button>
        </Link>
      }
    />
  );
}

export const router = createBrowserRouter(
  [
    { path: '/login', element: <Login /> },
    // 手机打开落地页：电脑端「手机打开」二维码指向这里，凭一次性票据兑换登录态
    { path: '/m-enter', element: <MobileEnter /> },
    // 旧「手机扫码确认页」已下线：确认只能在已登录的电脑端「申请码确认」里完成（路径沿用，避免旧链接失效）
    { path: '/screen-authorize', element: <Navigate to="/system/screen-scan" replace /> },
    {
      // 大屏全屏路由：不套 AppLayout、不走账号登录态门禁（屏端凭「设备凭据」独立换只读令牌），
      // 未授权时页内展示激活页（屏端凭设备凭据独立取数，与账号登录态无关）
      path: '/screen',
      element: (
        <Suspense fallback={null}>
          <ScreenPage />
        </Suspense>
      )
    },
    {
      path: '/',
      element: (
        <RequireAuth>
          <AppLayout />
        </RequireAuth>
      ),
      children: [
        { index: true, element: <Navigate to="/overview" replace /> },
        { path: 'overview', element: <RequirePerm path="overview"><PlatformOverview /></RequirePerm> },
        { path: 'tenants', element: <RequirePerm path="tenants"><Tenants /></RequirePerm> },
        { path: 'system/screen-devices', element: <RequirePerm path="system/screen-devices"><ScreenDevices /></RequirePerm> },
        { path: 'system/screen-scan', element: <RequirePerm path="system/screen-scan"><ScreenScan /></RequirePerm> },
        // 旧「大屏密钥」入口已下线（密钥通道取消）：保留重定向，避免收藏夹里的旧链接 404
        { path: 'system/screen-keys', element: <Navigate to="/system/screen-devices" replace /> },
        { path: 'system/app-versions', element: <RequirePerm path="system/app-versions"><AppVersions /></RequirePerm> },
        { path: 'system/app-releases', element: <RequirePerm path="system/app-releases"><AppReleases /></RequirePerm> },
        { path: 'region', element: <RequirePerm path="region"><Regions /></RequirePerm> },
        { path: 'platform', element: <RequirePerm path="platform"><Platform /></RequirePerm> },
        // 代管租户配置：平台运营可跨租户维护机构 / 账号 / 审批流
        { path: 'system/orgs', element: <OrgManage /> },
        { path: 'system/org-groups', element: <OrgGroups /> },
        { path: 'system/org-structure', element: <OrgStructure /> },
        { path: 'system/org-space', element: <OrgSpace /> },
        { path: 'system/users', element: <RequirePerm path="system/users"><SystemUsers /></RequirePerm> },
        { path: 'system/approval', element: <RequirePerm path="system/approval"><ApprovalFlows /></RequirePerm> },
        { path: 'system/org-config', element: <RequirePerm path="system/org-config"><OrgConfig /></RequirePerm> },
        { path: 'system/audit', element: <RequirePerm path="system/audit"><AuditLogs /></RequirePerm> },
        { path: '*', element: <NotFound /> }
      ]
    }
  ],
  {
    // 提前启用 v7 行为，消除控制台 future-flag 警告（行为与 v7 一致，不是仅静音）
    future: {
      v7_relativeSplatPath: true
    }
  }
);
